import { fetchRevisionUsers } from '@/api/maintenance-tickets-api'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import Button from '@mui/material/Button'
import Modal from '@mui/material/Modal'
import { styled } from '@mui/material/styles'
import { FC, useState } from 'react'
import useSWR from 'swr'

interface DenyModalProps {
  open: boolean
  onClose: () => void
  onDeny: (ticketId: number, note: string) => void
  data?: any
  onRevise: (ticketId: number, note: string, selectedUser: number) => void
  onDelete: (ticketId: number, note: string) => void
  mutate: () => Promise<any>
}

// Styled Components
const StyledModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const ModalContent = styled(Box)({
  width: 600,
  height: 600,
  backgroundColor: '#ffffff',
  borderRadius: 12,
  boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.1)',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
  overflow: 'auto',
})

const ModalTitle = styled(Typography)({
  fontSize: '22px',
  fontWeight: 500,
  fontFamily: 'Gabarito',
  color: '#505470',
  marginLeft: 1,
})

const CustomTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderWidth: 2,
    },
    '&:hover fieldset': {
      borderWidth: 2,
    },
    '&.Mui-focused fieldset': {
      borderWidth: 2,
    },
  },
})

const ActionButton = styled(Button)({
  textTransform: 'none',
  borderRadius: 8,
  width: 187,
  height: 47,
  minWidth: 187,
  minHeight: 47,
  fontSize: '18px',
  fontFamily: 'Gabarito',
  fontWeight: 600,
})

const ButtonGroup = styled(DialogActions)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 20,
  marginBottom: 20,
  marginLeft: 20,
  marginRight: 25, // 25px margin right for desktop
  gap: 8,
  flexDirection: 'row',
  '& > :not(style) ~ :not(style)': {
    marginLeft: 0,
  },
  '@media (max-width: 600px)': {
    flexDirection: 'column-reverse',
    marginLeft: 18,
    marginRight: 18,
    '& > *': {
      width: '100%',
      marginBottom: 8,
    },
    '& > :not(style) ~ :not(style)': {
      marginLeft: 0,
    },
  },
})

export const DenyModal: FC<DenyModalProps> = ({
  data,
  open,
  onClose,
  onDeny,
  onRevise,
  onDelete,
  mutate,
  isNested,
}) => {
  const [selectedType, setSelectedType] = useState<string>('For Revision')
  const [remarks, setRemarks] = useState('')
  const [selectedUser, setSelectedUser] = useState<number | ''>('')
  const [error, setError] = useState<string>('')
  const [errorRemarks, setErrorRemarks] = useState<string>('')

  const getAllForRevisionUsers = async () => {
    try {
      const response = await fetchRevisionUsers(data.id)
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch users:', error)
      throw error
    }
  }

  const { data: revisionUsers, isLoading } = useSWR(
    `/v1/ticket/for-revision/user-selection?ticketID=${data.id}`,
    getAllForRevisionUsers,
    { revalidateOnFocus: false },
  )

  console.log('revision users here ', revisionUsers)

  const isMaintenanceEngineer = (user: any) => {
    const role = (user?.roleName || user?.role || '').toString().toLowerCase()
    return role.includes('maintenance engineer')
  }

  const meUsers = revisionUsers?.filter(isMaintenanceEngineer) ?? []
  const otherUsers = revisionUsers?.filter((user: any) => !isMaintenanceEngineer(user)) ?? []
  const meUser = meUsers[0]

  const handleReject = () => {
    if (selectedType === 'For Revision' && !selectedUser) {
      if (!remarks) {
        setErrorRemarks('Please input some remarks.')
      }
      setError('Please select a user')
      return
    }

    if (!remarks) {
      setErrorRemarks('Please input some remarks.')
      return
    }
    setError('')
    setErrorRemarks('')

    if (selectedType === 'For Closure') {
      onDeny(data.id, remarks)
    } else if (selectedType === 'For Deletion') {
      onDelete(data.id, remarks)
    } else {
      onRevise(data.id, remarks, selectedUser as number)
    }
    mutate()
  }

  console.log('deny data here ', data)
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={!isNested}
      maxWidth={!isNested ? 'sm' : false}
      PaperProps={{
        sx: isNested ? { width: 'auto', maxWidth: 'unset', minWidth: 650 } : {},
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: '2rem', pt: '1.5rem' }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              sx={{
                fontFamily: 'Gabarito',
                fontWeight: 500,
                fontSize: '22px',
                color: '#505470',
              }}
            >
              Reject Ticket
            </Typography>
            <IconButton
              size="small"
              aria-label="close"
              sx={{
                color: '#505470',
                width: 20,
                height: 20,
              }}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ my: 2, borderWidth: 2, color: '#B1B8BF' }} />
        </Box>
      </DialogTitle>

      {/* Modal Body */}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'auto',
          px: 4,
          pt: 3,
          pb: 0,
          minHeight: 300,
        }}
      >
        <Box sx={{ marginTop: 2 }}>
          <Typography sx={{ marginBottom: 1 }}>
            Type <span style={{ color: 'red' }}>*</span>
          </Typography>
          <RadioGroup
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
          >
            <FormControlLabel
              value="For Revision"
              control={<Radio />}
              label="For Revision"
            />
            <FormControlLabel
              value="For Closure"
              control={<Radio />}
              label="For Cancellation"
            />
            <FormControlLabel
              value="For Deletion"
              control={<Radio />}
              label="For Deletion"
            />
          </RadioGroup>
        </Box>
        {selectedType === 'For Revision' && (
          <Box sx={{ marginTop: 2 }}>
            <Typography sx={{ marginBottom: 1 }}>
              Assign To <span style={{ color: 'red' }}>*</span>
            </Typography>
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Select
                value={selectedUser}
                onChange={(e) => {
                  setSelectedUser(e.target.value as number)
                  setError('')
                }}
                displayEmpty
                fullWidth
              >
                <MenuItem value="" disabled>
                  Select a user
                </MenuItem>
                {meUser && (
                  <MenuItem key={meUser.sourceUserId} value={meUser.sourceUserId}>
                    {meUser.roleName || meUser.role || 'Maintenance Engineers (ME)'}
                  </MenuItem>
                )}
                {otherUsers.map((user: any) => (
                  <MenuItem key={user.sourceUserId} value={user.sourceUserId}>
                    {user.fullNameWithRole || user.fullName || user.name || user.email || `User ${user.sourceUserId}`}
                  </MenuItem>
                ))}
              </Select>
            )}
            {error && <Typography color="error">{error}</Typography>}
          </Box>
        )}
        {selectedType === 'For Deletion' && (
          <Typography sx={{ color: 'error.main', mt: 1, fontSize: '13px' }}>
            This will remove the ticket from all views and cannot be undone.
          </Typography>
        )}
        {/* Remarks Section */}
        <Box sx={{ marginTop: 2 }}>
          <Typography sx={{ marginBottom: 1 }}>
            Remarks <span style={{ color: 'red' }}>*</span>
          </Typography>
          <CustomTextField
            value={remarks}
            onChange={(e) => {
              setRemarks(e.target.value)
              setErrorRemarks('')
            }}
            fullWidth
            multiline
            maxRows={3}
            variant="outlined"
            placeholder="Enter remarks here..."
          />
          {errorRemarks && (
            <Typography color="error">{errorRemarks}</Typography>
          )}
        </Box>
      </DialogContent>

      {/* Action Buttons */}
      <ButtonGroup>
        {' '}
        {/* Added marginTop for spacing */}
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: 'black',
            color: 'black',
            '&:hover': {
              borderColor: 'black',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#FF2D2D',
            color: '#ffffff',
          }}
          onClick={handleReject}
        >
          {selectedType === 'For Revision' ? 'Submit' : selectedType === 'For Deletion' ? 'Delete' : 'Reject'}
        </Button>
      </ButtonGroup>
    </Dialog>
  )
}
